import React from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarHeader } from './SidebarHeader';
import { SidebarModuleSwitcher } from './SidebarModuleSwitcher';
import { SidebarSearch } from './SidebarSearch';
import { SidebarActions } from './SidebarActions';
import { SidebarFilters } from './SidebarFilters';
import { SidebarVacancyList } from './SidebarVacancyList';
import { SidebarUserProfile } from './SidebarUserProfile';
import { SidebarAtsFolders } from './SidebarAtsFolders';
import { useAuthStore } from '../../store/authStore';
import { useVacanciesData } from '../../hooks/useVacanciesData';
import { useAtsNavigationStore } from '../../ats/store/atsNavigationStore';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onUserProfileClick?: () => void;
}

export function Sidebar({
  isCollapsed,
  onToggle,
  onUserProfileClick,
}: SidebarProps) {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const isAtsRoute = location.pathname === '/ats' || location.pathname.startsWith('/ats/');
  const isAtsSidebarExpanded = useAtsNavigationStore((state) => state.isAtsSidebarExpanded);

  // Загружаем вакансии из API
  const { isLoading } = useVacanciesData();

  if (isCollapsed && isAtsRoute) {
    return (
      <div
        className="
          flex h-full w-12 shrink-0 flex-col items-center
          border-r border-sidebar-border bg-sidebar-bg
          transition-all duration-300 ease-in-out
        "
      >
        <button
          type="button"
          data-testid="sidebar-expand-toggle"
          onClick={onToggle}
          aria-label="Развернуть сайдбар"
          className="mt-4 flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/70 transition-colors"
        >
          <img src="/icons/sidebar_icon.png" alt="" width="16" height="16" className="rotate-180" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`
        bg-sidebar-bg border-r border-sidebar-border
        flex flex-col font-inter-tight
        transition-all duration-300 ease-in-out
        overflow-hidden h-full
        ${isAtsRoute && !isCollapsed ? 'max-sm:absolute max-sm:inset-y-0 max-sm:left-0 max-sm:z-40' : ''}
        ${isCollapsed ? 'w-0' : isAtsRoute && isAtsSidebarExpanded ? 'w-[480px] max-w-[calc(100vw-48px)]' : 'w-sidebar'}
      `}
    >
      <div className="flex flex-col">
        <SidebarHeader isCollapsed={isCollapsed} onToggle={onToggle} />
        {!isCollapsed && <SidebarModuleSwitcher />}
        {!isCollapsed && !isAtsRoute && <SidebarSearch />}
      </div>

      {!isCollapsed && (
        <>
          {!isAtsRoute && (
            <>
              <SidebarActions />

              <SidebarFilters />

              <SidebarVacancyList isLoading={isLoading} />
            </>
          )}

          {isAtsRoute && <SidebarAtsFolders />}

          <SidebarUserProfile
            user={{
              name: user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.firstName || user?.lastName || undefined,
              email: user?.email,
              avatar: user?.avatarUrl || undefined,
            }}
            onClick={onUserProfileClick}
          />
        </>
      )}
    </div>
  );
}
